import React, { useEffect } from "react";
import Layout from "@/components/Layout";
import { useSEO } from "@/hooks/useSEO";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Shield, CheckCircle, Users, BookOpen } from "lucide-react";
import { addSchemaToHead } from "@/utils/schemaMarkup";

const EditorialPolicy: React.FC = () => {
	useSEO({
		title: "Editorial Policy — Florida Highwaymen Auction",
		description: "Our editorial standards for accuracy, sourcing, corrections, and expert review for Florida Highwaymen content.",
		canonicalUrl: "/editorial-policy",
		ogType: 'article'
	});

	useEffect(() => {
		const policy = {
			"@context": "https://schema.org",
			"@type": "WebPage",
			"name": "Editorial Policy",
			"description": "Editorial standards including sourcing, expert review, corrections, and updates.",
			"about": ["Accuracy", "Sourcing", "Corrections", "Expert Review"]
		};
		addSchemaToHead(policy);
	}, []);

	return (
		<Layout>
			<div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
				<div className="container mx-auto px-4 py-12 max-w-4xl">
					<h1 className="text-4xl font-light text-white tracking-wide mb-8">Editorial Policy</h1>
					<Card className="bg-white/5 backdrop-blur-xl border border-white/10">
						<CardHeader>
							<CardTitle className="text-white font-light flex items-center gap-2"><Shield className="h-5 w-5 text-emerald-400" /> Standards</CardTitle>
						</CardHeader>
						<CardContent className="text-white/80 font-light space-y-4">
							<p>We prioritize factual accuracy, primary sources, and clear attribution. Historical context is validated by knowledgeable contributors.</p>
							<p>Articles may be updated as new information emerges. Substantive corrections are timestamped.</p>
						</CardContent>
					</Card>

					<Separator className="my-8 bg-white/20" />

					<Card className="bg-white/5 backdrop-blur-xl border border-white/10">
						<CardHeader>
							<CardTitle className="text-white font-light flex items-center gap-2"><CheckCircle className="h-5 w-5 text-emerald-400" /> Review & Corrections</CardTitle>
						</CardHeader>
						<CardContent className="text-white/80 font-light space-y-4">
							<p>Expert reviewers evaluate select articles for accuracy. Readers can submit corrections via our contact page.</p>
						</CardContent>
					</Card>

					<Separator className="my-8 bg-white/20" />

					<Card className="bg-white/5 backdrop-blur-xl border border-white/10">
						<CardHeader>
							<CardTitle className="text-white font-light flex items-center gap-2"><Users className="h-5 w-5 text-emerald-400" /> Contributors</CardTitle>
						</CardHeader>
						<CardContent className="text-white/80 font-light space-y-4">
							<p>Content is produced by our in-house team and vetted partners with experience in Florida Highwaymen scholarship and art handling.</p>
						</CardContent>
					</Card>

					<Separator className="my-8 bg-white/20" />

					<Card className="bg-white/5 backdrop-blur-xl border border-white/10">
						<CardHeader>
							<CardTitle className="text-white font-light flex items-center gap-2"><BookOpen className="h-5 w-5 text-emerald-400" /> Sources</CardTitle>
						</CardHeader>
						<CardContent className="text-white/80 font-light space-y-4">
							<p>We reference auction records, archives, artist interviews, museum publications, and reputable books. Citations are added where possible.</p>
						</CardContent>
					</Card>
				</div>
			</div>
		</Layout>
	);
};

export default EditorialPolicy;











